import { Error } from '@redux/common/types';
import { getAuthPersonSelector } from '@redux/oauth/oauthSelectors';
import { isSuperAdminSelector, getLoggedInPerson } from '@redux/person/personSelector';
import { PERMISSION_ROLE_ID_CAREGIVER } from '@redux/person/personTypes';
import { ReduxState } from '@redux/types';
import { isListNotEmpty } from 'utils/util';
import { addNewOrganizationAction, updateOrganizationAction } from './organizationActions';
import { Organization } from './organizationTypes';
import { loadSelectedOrganizationId } from './organizationReducer';

export const getOrganizationStateSelector = (state: ReduxState) => {
  return state.organization;
};

export const getOrganizationByIdSelector = (state: ReduxState): Record<string, Organization> => {
  return getOrganizationStateSelector(state).organizationById;
};

export const getSelectedOrganizationIdSelector = (state: ReduxState): Nullable<string> => {
  const selectedPersistentOrgId = loadSelectedOrganizationId();
  return selectedPersistentOrgId !== null ? selectedPersistentOrgId : getOrganizationStateSelector(state).selectedOrganizationId;
};

/**
 * Returns the active organization ID for the current user context.
 * For super admins: returns the selected organization ID if set, otherwise falls back to their own org
 * For regular users: always returns their own organization ID
 */
export const getActiveOrganizationIdSelector = (state: ReduxState): string => {
  const isSuperAdmin = isSuperAdminSelector(state);
  const selectedOrgId = getSelectedOrganizationIdSelector(state);
  const authPerson = getAuthPersonSelector(state);
  const userOrgId = authPerson?.organizationId ?? '';

  // Super admins can use selected org, otherwise use their own org
  if (isSuperAdmin && selectedOrgId) {
    return selectedOrgId;
  }
  return userOrgId;
};

/**
 * Returns the active organization object for the current user context
 */
export const getActiveOrganizationSelector = (state: ReduxState): Nullable<Organization> => {
  const activeOrgId = getActiveOrganizationIdSelector(state);
  const organizationById = getOrganizationByIdSelector(state);
  return activeOrgId ? organizationById[activeOrgId] : null;
};

/**
 * Returns whether the logged-in user is a caregiver in a DTC organization
 * This checks both:
 * 1. The active organization's accountType is 'DTC'
 * 2. The logged-in user has the Caregiver role
 */
export const isDTCOrganizationCaregiverSelector = (state: ReduxState): boolean => {
  const activeOrganization = getActiveOrganizationSelector(state);
  const loggedInPerson = getLoggedInPerson(state);

  // Check if organization is DTC
  const isDTCOrg = activeOrganization?.accountType === 'DTC';
  if (!isDTCOrg) {
    return false;
  }

  // Check if user has Caregiver role
  if (loggedInPerson && loggedInPerson.personPermissionRoles && isListNotEmpty(loggedInPerson.personPermissionRoles)) {
    for (const personPermissionRole of loggedInPerson.personPermissionRoles) {
      if (personPermissionRole.permissionRoleId === PERMISSION_ROLE_ID_CAREGIVER) {
        return true;
      }
    }
  }

  return false;
};

export const getIsLoadingSelector = (state: ReduxState): Record<string, boolean> => {
  return getOrganizationStateSelector(state).isLoading;
};

export const isLoadingAddNewOrganizationSelector = (state: ReduxState): boolean => {
  return getIsLoadingSelector(state)[addNewOrganizationAction.start.type];
};

export const isLoadingUpdateOrganizationSelector = (state: ReduxState): boolean => {
  return getIsLoadingSelector(state)[updateOrganizationAction.start.type];
};

export const getErrorSelector = (state: ReduxState): Record<string, Error> => {
  return getOrganizationStateSelector(state).error;
};

export const getSuccessSelector = (state: ReduxState): Record<string, boolean> => {
  return getOrganizationStateSelector(state).success;
};

export const getOrganizationFeaturesSelector = (state: ReduxState) => {
  return getOrganizationStateSelector(state).organizationFeatures;
};
